/**
 * EmployeeOnboardingWizard — Phase 3 stepped create wizard.
 *
 * Two paths:
 *   Employee (On-Roll) — 6 steps
 *   Consultant         — 4 steps
 *
 * Edit path is UNCHANGED (existing EmployeeFormModal in EmployeeMaster.jsx).
 * This wizard submits to the same POST /hr/employees endpoint.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { ChevronRight, ChevronLeft, Check, Loader2, X, User, Briefcase, Building2, Shield, Eye, AlertTriangle } from 'lucide-react';
import { formatDate } from '../lib/format';
import { toast } from 'sonner';
import { apiGet, apiPost } from '../lib/api';

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────
const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const GENDERS = ['Male', 'Female', 'Other', 'Prefer not to say'];
const PAYMENT_BASES = ['Monthly Retainer', 'Per Visit', 'Per Milestone', 'Hourly'];

const EMPTY_EMPLOYEE = {
  employment_type: 'On-Roll',
  // Step 1 — Identity
  name: '', gender: '', dob: '', phone: '', email: '',
  emergency_contact: '', address: '', blood_group: '',
  // Step 2 — Employment
  doj: '', department: '', designation: '', grade: '',
  reporting_manager_id: '', reporting_manager_name: '',
  posting_type: '', office_location_id: '',
  probation_period_days: 90, employment_status: 'Probation',
  referred_by_employee_id: '',
  // Step 3 — Statutory
  pan: '', aadhaar: '',
  pf_no: '', uan: '', esi_no: '',
  _pf_tba: false, _uan_tba: false, _esi_tba: false,
  bank_name: '', bank_account: '', ifsc: '',
  // Step 4 — Compensation
  gross_salary: '', annual_ctc: '', opt_out_pf: false, opt_out_pf_cap: false,
  // Step 5 — Access
  senior_profile: '', project_ids: [], all_project_access: false,
  consultant_profile: '', contract_end_date: '',
  status: 'Active',
};

const EMPTY_CONSULTANT = {
  employment_type: 'Consultant',
  // Step 1 — Identity & Firm
  name: '', phone: '', email: '', address: '', pan: '',
  gstin: '', firm_name: '',
  // Step 2 — Engagement
  doj: '', contract_end_date: '', scope: '',
  gross_salary: '', consultant_payment_basis: 'Monthly Retainer',
  reporting_manager_id: '', reporting_manager_name: '',
  // Step 3 — Access
  consultant_profile: '', project_ids: [], all_project_access: false,
  department: 'Consultants',
  status: 'Active',
};

function FieldError({ msg }) {
  if (!msg) return null;
  return <p className="text-xs text-rose-500 mt-0.5">{msg}</p>;
}

function Label({ children, required }) {
  return (
    <label className="text-xs font-display font-semibold text-slate-600 mb-1 block">
      {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
  );
}

function Input({ label, required, error, ...props }) {
  return (
    <div>
      {label && <Label required={required}>{label}</Label>}
      <input
        className={`w-full border rounded-sm px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:border-[#3b4872] ${error ? 'border-rose-400' : 'border-slate-300'}`}
        {...props}
      />
      <FieldError msg={error} />
    </div>
  );
}

function Select({ label, required, error, children, ...props }) {
  return (
    <div>
      {label && <Label required={required}>{label}</Label>}
      <select
        className={`w-full border rounded-sm px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:border-[#3b4872] ${error ? 'border-rose-400' : 'border-slate-300'}`}
        {...props}>
        {children}
      </select>
      <FieldError msg={error} />
    </div>
  );
}

// Step progress bar
function StepBar({ steps, current }) {
  return (
    <div className="flex items-center px-6 pt-5 pb-4 border-b border-slate-100">
      {steps.map((s, i) => (
        <React.Fragment key={s.key}>
          <div className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-colors ${i < current ? 'bg-[#3b4872] border-[#3b4872] text-white' : i === current ? 'border-[#3b4872] text-[#3b4872]' : 'border-slate-200 text-slate-400'}`}>
              {i < current ? <Check size={12} /> : i + 1}
            </div>
            <span className={`text-xs font-display font-semibold hidden sm:block ${i === current ? 'text-[#3b4872]' : i < current ? 'text-slate-500' : 'text-slate-300'}`}>{s.label}</span>
          </div>
          {i < steps.length - 1 && (
            <div className={`flex-1 h-px mx-2 ${i < current ? 'bg-[#3b4872]' : 'bg-slate-200'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// Main Wizard
// ─────────────────────────────────────────────────────────────
export default function EmployeeOnboardingWizard({ onClose, onCreated, projects = [], officeLocations = [], allEmployees = [], deptOpts = [], deptNoProfile = [] }) {
  const [path, setPath] = useState(null); // 'employee' | 'consultant'
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [seniorProfiles, setSeniorProfiles] = useState([]);
  const [consultantProfiles, setConsultantProfiles] = useState([]);

  useEffect(() => {
    apiGet('/rbac/v2/senior-profiles').then(r => setSeniorProfiles(r.profiles || [])).catch(() => {});
    apiGet('/rbac/v2/consultant-profiles').then(r => setConsultantProfiles(r.profiles || [])).catch(() => {});
  }, []);

  const update = useCallback((field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: undefined }));
  }, []);

  const choosePath = (p) => {
    setPath(p);
    setForm(p === 'employee' ? { ...EMPTY_EMPLOYEE } : { ...EMPTY_CONSULTANT });
    setStep(0);
  };

  // ── Validation per step ────────────────────────────────
  const validate = (stepIdx) => {
    const errs = {};
    const f = form;
    if (path === 'employee') {
      if (stepIdx === 0) {
        if (!f.name?.trim()) errs.name = 'Name is required';
        if (!f.gender) errs.gender = 'Gender is required';
        if (!f.dob) errs.dob = 'Date of birth is required';
        if (!f.phone?.trim()) errs.phone = 'Phone is required';
        if (!f.email?.trim()) errs.email = 'Email is required';
      }
      if (stepIdx === 1) {
        if (!f.doj) errs.doj = 'Date of joining is required';
        if (!f.department) errs.department = 'Department is required';
        if (!f.designation?.trim()) errs.designation = 'Designation is required';
        if (!f.grade) errs.grade = 'Grade is required';
        if (!f.reporting_manager_id && !['MD', 'CMD', 'CEO'].includes(f.designation)) errs.reporting_manager_id = 'Reporting manager is required';
        if (!f.posting_type) errs.posting_type = 'Posting type is required';
        if (f.posting_type === 'Office' && !f.office_location_id) errs.office_location_id = 'Office location is required';
      }
      if (stepIdx === 2) {
        if (!f.pan?.trim()) errs.pan = 'PAN is required';
        if (!f.aadhaar?.trim()) errs.aadhaar = 'Aadhaar is required';
        if (!f.bank_name?.trim()) errs.bank_name = 'Bank name is required';
        if (!f.bank_account?.trim()) errs.bank_account = 'Account number is required';
        if (!f.ifsc?.trim()) errs.ifsc = 'IFSC is required';
      }
      if (stepIdx === 3) {
        if (!f.gross_salary || parseFloat(f.gross_salary) <= 0) errs.gross_salary = 'Gross salary is required';
      }
    }
    if (path === 'consultant') {
      if (stepIdx === 0) {
        if (!f.name?.trim()) errs.name = 'Name is required';
        if (!f.phone?.trim()) errs.phone = 'Phone is required';
        if (!f.email?.trim()) errs.email = 'Email is required';
        if (!f.pan?.trim()) errs.pan = 'PAN is required (TDS 194J)';
      }
      if (stepIdx === 1) {
        if (!f.doj) errs.doj = 'Contract start date is required';
        if (!f.contract_end_date) errs.contract_end_date = 'Contract end date is required';
        if (!f.gross_salary || parseFloat(f.gross_salary) <= 0) errs.gross_salary = 'Fee amount is required';
        if (!f.consultant_payment_basis) errs.consultant_payment_basis = 'Fee basis is required';
        if (!f.reporting_manager_id) errs.reporting_manager_id = 'Reporting manager is required';
      }
      if (stepIdx === 2) {
        if (!f.consultant_profile) errs.consultant_profile = 'Consultant profile is required';
        if (!f.project_ids?.length) errs.project_ids = 'At least one project is required';
      }
    }
    return errs;
  };

  const goNext = () => {
    const errs = validate(step);
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setStep(s => s + 1);
  };

  const goBack = () => setStep(s => s - 1);

  // ── Submit ─────────────────────────────────────────────
  const FIELD_STEP = {
    employee: {
      name: 0, gender: 0, dob: 0, phone: 0, email: 0, emergency_contact: 0, address: 0, blood_group: 0,
      doj: 1, department: 1, designation: 1, grade: 1, reporting_manager_id: 1, posting_type: 1,
      office_location_id: 1, probation_period_days: 1, referred_by_employee_id: 1,
      pan: 2, aadhaar: 2, pf_no: 2, uan: 2, esi_no: 2, bank_name: 2, bank_account: 2, ifsc: 2,
      gross_salary: 3, annual_ctc: 3, opt_out_pf: 3, opt_out_pf_cap: 3,
      senior_profile: 4, project_ids: 4, all_project_access: 4, consultant_profile: 4, contract_end_date: 4,
    },
    consultant: {
      name: 0, phone: 0, email: 0, pan: 0, gstin: 0, firm_name: 0, address: 0,
      doj: 1, contract_end_date: 1, gross_salary: 1, consultant_payment_basis: 1, reporting_manager_id: 1, scope: 1,
      consultant_profile: 2, project_ids: 2,
    },
  };

  // Round 3 Item 2 — jump the wizard back to the step containing the failing
  // field (or Employment, for the "Department has no permission profile"
  // guard) instead of leaving the user stuck on Review with just a toast.
  const jumpToFailingStep = (detail) => {
    const map = FIELD_STEP[path] || {};
    if (Array.isArray(detail)) {
      for (const d of detail) {
        const field = d?.loc?.[d.loc.length - 1];
        if (field && map[field] !== undefined) { setStep(map[field]); return; }
      }
    } else if (typeof detail === 'string' && /permission profile/i.test(detail)) {
      setStep(map.department ?? 1);
    }
  };

  const submit = async () => {
    // Final-step validation
    const allErrs = {};
    const steps = path === 'employee' ? 6 : 4;
    for (let i = 0; i < steps - 1; i++) {
      Object.assign(allErrs, validate(i));
    }
    if (Object.keys(allErrs).length) { setErrors(allErrs); toast.error('Some required fields are missing — check earlier steps'); return; }

    setSaving(true);
    try {
      const payload = { ...form };
      // Clean "to be allotted" flags
      if (payload._pf_tba) { payload.pf_no = 'TBA'; } delete payload._pf_tba;
      if (payload._uan_tba) { payload.uan = 'TBA'; } delete payload._uan_tba;
      if (payload._esi_tba) { payload.esi_no = 'TBA'; } delete payload._esi_tba;
      delete payload.scope;
      // Round 3 Item 2 — "Auto-computed if blank" must actually mean blank is
      // allowed: strip empty-string numerics to null so Pydantic's
      // Optional[float] doesn't 422 before the backend's auto-compute runs.
      for (const k of ['annual_ctc', 'gross_salary', 'probation_period_days', 'referee_gross_for_bonus', 'consultant_fixed_amount']) {
        if (payload[k] === '') payload[k] = null;
      }

      const emp = await apiPost('/hr/employees', payload);
      toast.success(`${path === 'consultant' ? 'Consultant' : 'Employee'} created: ${emp.name}`);
      onCreated(emp);
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Creation failed';
      const msg = Array.isArray(detail) ? (detail[0]?.msg || 'Some fields are invalid') : detail;
      toast.error(msg);
      jumpToFailingStep(detail);
    }
    setSaving(false);
  };

  // ── Step definitions ────────────────────────────────────
  const employeeSteps = [
    { key: 'identity', label: 'Identity' },
    { key: 'employment', label: 'Employment' },
    { key: 'statutory', label: 'Statutory' },
    { key: 'compensation', label: 'Compensation' },
    { key: 'access', label: 'Access' },
    { key: 'review', label: 'Review' },
  ];
  const consultantSteps = [
    { key: 'identity', label: 'Identity & Firm' },
    { key: 'engagement', label: 'Engagement' },
    { key: 'access', label: 'Access' },
    { key: 'review', label: 'Review' },
  ];
  const steps = path === 'employee' ? employeeSteps : consultantSteps;

  // ────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="onboarding-wizard">
      <div className="bg-white rounded-sm border border-slate-200 w-full max-w-2xl max-h-[92vh] flex flex-col shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="font-display font-bold text-sm text-[#3b4872]">
            {!path ? 'New Employee / Consultant' : path === 'employee' ? 'Onboard Employee (On-Roll)' : 'Onboard Consultant'}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
        </div>

        {/* Path selector */}
        {!path && (
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="grid grid-cols-2 gap-6 w-full max-w-md">
              <button onClick={() => choosePath('employee')}
                className="border-2 border-slate-200 hover:border-[#3b4872] rounded-sm p-6 text-center transition-colors group"
                data-testid="wizard-path-employee">
                <User size={32} className="mx-auto mb-3 text-slate-400 group-hover:text-[#3b4872]" />
                <div className="font-display font-bold text-slate-800">Employee</div>
                <div className="text-xs text-slate-400 mt-1">On-roll staff — full onboarding with payroll & statutory</div>
              </button>
              <button onClick={() => choosePath('consultant')}
                className="border-2 border-slate-200 hover:border-[#3b4872] rounded-sm p-6 text-center transition-colors group"
                data-testid="wizard-path-consultant">
                <Briefcase size={32} className="mx-auto mb-3 text-slate-400 group-hover:text-[#3b4872]" />
                <div className="font-display font-bold text-slate-800">Consultant</div>
                <div className="text-xs text-slate-400 mt-1">External contractor — narrow access, contract expiry</div>
              </button>
            </div>
          </div>
        )}

        {/* Steps */}
        {path && (
          <>
            <StepBar steps={steps} current={step} />
            <div className="flex-1 overflow-y-auto p-6">
              {path === 'employee' && <EmployeeSteps step={step} form={form} update={update} errors={errors}
                projects={projects} officeLocations={officeLocations} allEmployees={allEmployees}
                deptOpts={deptOpts} deptNoProfile={deptNoProfile} seniorProfiles={seniorProfiles} />}
              {path === 'consultant' && <ConsultantSteps step={step} form={form} update={update} errors={errors}
                projects={projects} allEmployees={allEmployees} consultantProfiles={consultantProfiles} />}
            </div>
            <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between bg-slate-50">
              <button onClick={step === 0 ? () => setPath(null) : goBack}
                className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800"
                data-testid="wizard-back">
                <ChevronLeft size={16} /> {step === 0 ? 'Change type' : 'Back'}
              </button>
              {step < steps.length - 1 ? (
                <button onClick={goNext}
                  className="flex items-center gap-1.5 px-4 py-2 bg-[#3b4872] text-white text-sm font-display font-bold rounded-sm"
                  data-testid="wizard-next">
                  Next <ChevronRight size={16} />
                </button>
              ) : (
                <button onClick={submit} disabled={saving}
                  className="flex items-center gap-1.5 px-5 py-2 bg-[#3b4872] text-white text-sm font-display font-bold rounded-sm disabled:opacity-50"
                  data-testid="wizard-submit">
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                  {path === 'consultant' ? 'Create Consultant' : 'Create Employee'}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// Employee Step Content
// ─────────────────────────────────────────────────────────────
function EmployeeSteps({ step, form, update, errors, projects, officeLocations, allEmployees, deptOpts, deptNoProfile, seniorProfiles }) {
  const [effectiveAccess, setEffectiveAccess] = useState(null);
  useEffect(() => {
    if (step !== 4 || !form.department) return;
    apiGet(`/rbac/v2/department-profiles/${encodeURIComponent(form.department)}`)
      .then(r => {
        const profiles = r.profiles || [];
        const p = profiles.find(x => x.department === form.department);
        setEffectiveAccess(p?.permissions || null);
      })
      .catch(() => setEffectiveAccess(null));
  }, [step, form.department]);

  if (step === 0) return (
    <div className="grid grid-cols-2 gap-4" data-testid="wizard-step-identity">
      <div className="col-span-2"><Input label="Full Name" required value={form.name} onChange={e => update('name', e.target.value)} error={errors.name} data-testid="wizard-name" /></div>
      <Select label="Gender" required value={form.gender} onChange={e => update('gender', e.target.value)} error={errors.gender} data-testid="wizard-gender">
        <option value="">— Select —</option>
        {GENDERS.map(g => <option key={g}>{g}</option>)}
      </Select>
      <Input label="Date of Birth" required type="date" value={form.dob} onChange={e => update('dob', e.target.value)} error={errors.dob} data-testid="wizard-dob" />
      <Input label="Mobile Phone" required value={form.phone} onChange={e => update('phone', e.target.value)} error={errors.phone} data-testid="wizard-phone" />
      <Input label="Work Email" required type="email" value={form.email} onChange={e => update('email', e.target.value)} error={errors.email} data-testid="wizard-email" />
      <Input label="Emergency Contact" value={form.emergency_contact} onChange={e => update('emergency_contact', e.target.value)} data-testid="wizard-emergency" />
      <Select label="Blood Group" value={form.blood_group} onChange={e => update('blood_group', e.target.value)}>
        <option value="">— Optional —</option>
        {BLOOD_GROUPS.map(b => <option key={b}>{b}</option>)}
      </Select>
      <div className="col-span-2"><Input label="Address" value={form.address} onChange={e => update('address', e.target.value)} /></div>
    </div>
  );

  if (step === 1) return (
    <div className="grid grid-cols-2 gap-4" data-testid="wizard-step-employment">
      <Input label="Date of Joining" required type="date" value={form.doj} onChange={e => update('doj', e.target.value)} error={errors.doj} data-testid="wizard-doj" />
      <Select label="Department" required value={form.department} onChange={e => update('department', e.target.value)} error={errors.department} data-testid="wizard-department">
        <option value="">— Select —</option>
        {deptOpts.map(d => <option key={d} disabled={deptNoProfile.includes(d)}>{d}{deptNoProfile.includes(d) ? ' (no permission profile)' : ''}</option>)}
      </Select>
      {deptNoProfile.includes(form.department) && (
        <p className="text-[11px] text-amber-600 -mt-3" data-testid="wizard-department-no-profile-warning">
          ⚠ "{form.department}" has no matching permission profile — saving will fail. Pick a different department.
        </p>
      )}
      <Input label="Designation" required value={form.designation} onChange={e => update('designation', e.target.value)} error={errors.designation} data-testid="wizard-designation" />
      <Input label="Grade" required value={form.grade} onChange={e => update('grade', e.target.value)} error={errors.grade} data-testid="wizard-grade" />
      <div className="col-span-2">
        <Label required>Reporting Manager</Label>
        <select value={form.reporting_manager_id} onChange={e => {
          const emp = allEmployees.find(x => x.id === e.target.value);
          update('reporting_manager_id', e.target.value);
          update('reporting_manager_name', emp?.name || '');
        }} className={`w-full border rounded-sm px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:border-[#3b4872] ${errors.reporting_manager_id ? 'border-rose-400' : 'border-slate-300'}`} data-testid="wizard-manager">
          <option value="">— Select —</option>
          {allEmployees.map(e => <option key={e.id} value={e.id}>{e.name} ({e.designation})</option>)}
        </select>
        <FieldError msg={errors.reporting_manager_id} />
      </div>
      <Select label="Posting Type" required value={form.posting_type} onChange={e => update('posting_type', e.target.value)} error={errors.posting_type} data-testid="wizard-posting-type">
        <option value="">— Select —</option>
        <option>Office</option>
        <option>Site</option>
      </Select>
      {form.posting_type === 'Office' ? (
        <Select label="Office Location" required value={form.office_location_id} onChange={e => update('office_location_id', e.target.value)} error={errors.office_location_id}>
          <option value="">— Select —</option>
          {officeLocations.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
        </Select>
      ) : (
        <Select label="Project" value={form.project_id} onChange={e => update('project_id', e.target.value)}>
          <option value="">— Optional —</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
      )}
      <Input label="Probation (days)" type="number" value={form.probation_period_days} onChange={e => update('probation_period_days', parseInt(e.target.value) || 90)} />
      <Select label="Referred By (optional)" value={form.referred_by_employee_id} onChange={e => update('referred_by_employee_id', e.target.value)}>
        <option value="">— None —</option>
        {allEmployees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
      </Select>
    </div>
  );

  if (step === 2) return (
    <div className="grid grid-cols-2 gap-4" data-testid="wizard-step-statutory">
      <Input label="PAN" required value={form.pan} onChange={e => update('pan', e.target.value.toUpperCase())} error={errors.pan} data-testid="wizard-pan" />
      <Input label="Aadhaar" required value={form.aadhaar} onChange={e => update('aadhaar', e.target.value)} error={errors.aadhaar} data-testid="wizard-aadhaar" />
      <div>
        <Label>UAN</Label>
        <input value={form._uan_tba ? '' : form.uan} onChange={e => update('uan', e.target.value)} disabled={form._uan_tba}
          placeholder={form._uan_tba ? 'To be allotted' : ''} className="w-full border border-slate-300 rounded-sm px-2.5 py-1.5 text-sm focus:outline-none focus:border-[#3b4872] disabled:bg-slate-50" />
        <label className="flex items-center gap-1 text-[10px] text-slate-500 mt-0.5 cursor-pointer">
          <input type="checkbox" checked={form._uan_tba} onChange={e => update('_uan_tba', e.target.checked)} className="accent-[#3b4872]" /> To be allotted
        </label>
      </div>
      <div>
        <Label>PF No</Label>
        <input value={form._pf_tba ? '' : form.pf_no} onChange={e => update('pf_no', e.target.value)} disabled={form._pf_tba}
          placeholder={form._pf_tba ? 'To be allotted' : ''} className="w-full border border-slate-300 rounded-sm px-2.5 py-1.5 text-sm focus:outline-none focus:border-[#3b4872] disabled:bg-slate-50" />
        <label className="flex items-center gap-1 text-[10px] text-slate-500 mt-0.5 cursor-pointer">
          <input type="checkbox" checked={form._pf_tba} onChange={e => update('_pf_tba', e.target.checked)} className="accent-[#3b4872]" /> To be allotted
        </label>
      </div>
      <div>
        <Label>ESI No</Label>
        <input value={form._esi_tba ? '' : form.esi_no} onChange={e => update('esi_no', e.target.value)} disabled={form._esi_tba}
          placeholder={form._esi_tba ? 'To be allotted' : ''} className="w-full border border-slate-300 rounded-sm px-2.5 py-1.5 text-sm focus:outline-none focus:border-[#3b4872] disabled:bg-slate-50" />
        <label className="flex items-center gap-1 text-[10px] text-slate-500 mt-0.5 cursor-pointer">
          <input type="checkbox" checked={form._esi_tba} onChange={e => update('_esi_tba', e.target.checked)} className="accent-[#3b4872]" /> To be allotted
        </label>
      </div>
      <div className="col-span-2 border-t border-slate-100 pt-4 mt-2">
        <p className="text-xs font-display font-semibold text-slate-600 mb-3">Bank Details</p>
      </div>
      <Input label="Bank Name" required value={form.bank_name} onChange={e => update('bank_name', e.target.value)} error={errors.bank_name} data-testid="wizard-bank-name" />
      <Input label="Account Number" required value={form.bank_account} onChange={e => update('bank_account', e.target.value)} error={errors.bank_account} data-testid="wizard-bank-account" />
      <Input label="IFSC Code" required value={form.ifsc} onChange={e => update('ifsc', e.target.value.toUpperCase())} error={errors.ifsc} data-testid="wizard-ifsc" />
    </div>
  );

  if (step === 3) return (
    <div className="grid grid-cols-2 gap-4" data-testid="wizard-step-compensation">
      <Input label="Gross Monthly Salary (₹)" required type="number" value={form.gross_salary} onChange={e => update('gross_salary', e.target.value)} error={errors.gross_salary} data-testid="wizard-gross" />
      <Input label="Annual CTC (₹)" type="number" value={form.annual_ctc} onChange={e => update('annual_ctc', e.target.value)} placeholder="Auto-computed if blank" />
      <div className="col-span-2">
        <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
          <input type="checkbox" checked={form.opt_out_pf} onChange={e => update('opt_out_pf', e.target.checked)} className="accent-[#3b4872]" />
          Opt out of PF (Form 11)
        </label>
      </div>
      {form.opt_out_pf && (
        <div className="col-span-2">
          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
            <input type="checkbox" checked={form.opt_out_pf_cap} onChange={e => update('opt_out_pf_cap', e.target.checked)} className="accent-[#3b4872]" />
            Contribute 12% on full basic (no ₹15k wage ceiling)
          </label>
        </div>
      )}
    </div>
  );

  if (step === 4) return (
    <div className="space-y-5" data-testid="wizard-step-access">
      <div className="bg-slate-50 border border-slate-200 rounded-sm p-4">
        <p className="text-xs font-display font-semibold text-slate-600 mb-1">Department Access</p>
        <p className="text-xs text-slate-500">Access is automatically determined by the <strong>{form.department || '—'}</strong> department profile. The permissions below show what this employee will have based on that profile.</p>
        {effectiveAccess && (
          <div className="mt-3 grid grid-cols-3 gap-1">
            {Object.entries(effectiveAccess).filter(([, v]) => v.level !== 'none').map(([key, v]) => (
              <div key={key} className="text-[10px] bg-white border border-slate-200 rounded px-2 py-1">
                <span className="font-mono text-slate-600">{key}</span>
                <span className={`ml-1 font-bold ${v.level === 'edit' ? 'text-[#3b4872]' : 'text-slate-400'}`}>{v.level}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <div>
        <Label>Senior Management Profile (optional)</Label>
        <select value={form.senior_profile} onChange={e => update('senior_profile', e.target.value)}
          className="w-full border border-slate-300 rounded-sm px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:border-[#3b4872]"
          data-testid="wizard-senior-profile">
          <option value="">— None —</option>
          {seniorProfiles.map(p => <option key={p.name} value={p.name}>{p.name}</option>)}
        </select>
      </div>
      <div>
        <Label>Project Access</Label>
        <label className="flex items-center gap-2 text-sm text-slate-600 mb-2 cursor-pointer">
          <input type="checkbox" checked={form.all_project_access} onChange={e => update('all_project_access', e.target.checked)} className="accent-[#3b4872]" />
          All projects (Head Office / coordinators)
        </label>
        {!form.all_project_access && (
          <div className="space-y-1 max-h-40 overflow-y-auto border border-slate-200 rounded-sm p-2">
            {projects.map(p => (
              <label key={p.id} className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer hover:bg-slate-50 px-1 py-0.5 rounded">
                <input type="checkbox"
                  checked={(form.project_ids || []).includes(p.id)}
                  onChange={e => {
                    const ids = new Set(form.project_ids || []);
                    e.target.checked ? ids.add(p.id) : ids.delete(p.id);
                    update('project_ids', [...ids]);
                  }} className="accent-[#3b4872]" />
                {p.name}
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  if (step === 5) return (
    <div className="space-y-4" data-testid="wizard-step-review">
      <p className="text-xs text-slate-500">Review all details before creating. Use Back to correct anything.</p>
      {[
        { label: 'Identity', fields: [['Name', form.name], ['Email', form.email], ['Phone', form.phone], ['Gender', form.gender], ['DOB', form.dob]] },
        { label: 'Employment', fields: [['DOJ', form.doj], ['Department', form.department], ['Designation', form.designation], ['Grade', form.grade], ['Posting', form.posting_type]] },
        { label: 'Statutory', fields: [['PAN', form.pan], ['Aadhaar', form.aadhaar ? form.aadhaar.replace(/\d(?=\d{4})/g, '*') : ''], ['Bank', form.bank_name], ['IFSC', form.ifsc]] },
        { label: 'Compensation', fields: [['Gross/mo', form.gross_salary ? `₹${Number(form.gross_salary).toLocaleString('en-IN')}` : '—'], ['Annual CTC', form.annual_ctc ? `₹${Number(form.annual_ctc).toLocaleString('en-IN')}` : 'Auto']] },
        { label: 'Access', fields: [['Dept Profile', form.department || '—'], ['Senior Profile', form.senior_profile || '—'], ['Projects', form.all_project_access ? 'All' : `${(form.project_ids || []).length} selected`]] },
      ].map(({ label, fields }) => (
        <div key={label} className="bg-slate-50 rounded-sm border border-slate-100 p-4">
          <p className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-2">{label}</p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            {fields.map(([k, v]) => v && (
              <div key={k} className="flex gap-1 text-xs">
                <span className="text-slate-400 w-24 shrink-0">{k}:</span>
                <span className="font-semibold text-slate-700 truncate">{v}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  return null;
}


// ─────────────────────────────────────────────────────────────
// Consultant Step Content
// ─────────────────────────────────────────────────────────────
function ConsultantSteps({ step, form, update, errors, projects, allEmployees, consultantProfiles }) {
  if (step === 0) return (
    <div className="grid grid-cols-2 gap-4" data-testid="wizard-step-consultant-identity">
      <div className="col-span-2"><Input label="Full Name" required value={form.name} onChange={e => update('name', e.target.value)} error={errors.name} data-testid="wizard-consultant-name" /></div>
      <Input label="Mobile Phone" required value={form.phone} onChange={e => update('phone', e.target.value)} error={errors.phone} />
      <Input label="Email" required type="email" value={form.email} onChange={e => update('email', e.target.value)} error={errors.email} />
      <Input label="PAN (TDS 194J)" required value={form.pan} onChange={e => update('pan', e.target.value.toUpperCase())} error={errors.pan} data-testid="wizard-consultant-pan" />
      <Input label="GSTIN (optional)" value={form.gstin || ''} onChange={e => update('gstin', e.target.value)} />
      <Input label="Firm / Company Name (optional)" value={form.firm_name || ''} onChange={e => update('firm_name', e.target.value)} />
      <div className="col-span-2"><Input label="Address" value={form.address} onChange={e => update('address', e.target.value)} /></div>
    </div>
  );

  if (step === 1) return (
    <div className="grid grid-cols-2 gap-4" data-testid="wizard-step-engagement">
      <Input label="Contract Start Date" required type="date" value={form.doj} onChange={e => update('doj', e.target.value)} error={errors.doj} data-testid="wizard-contract-start" />
      <Input label="Contract End Date" required type="date" value={form.contract_end_date} onChange={e => { update('contract_end_date', e.target.value); }} error={errors.contract_end_date} data-testid="wizard-contract-end" />
      <Input label="Fee Amount (₹)" required type="number" value={form.gross_salary} onChange={e => update('gross_salary', e.target.value)} error={errors.gross_salary} data-testid="wizard-fee-amount" />
      <Select label="Fee Basis" required value={form.consultant_payment_basis} onChange={e => update('consultant_payment_basis', e.target.value)} error={errors.consultant_payment_basis}>
        {PAYMENT_BASES.map(b => <option key={b}>{b}</option>)}
      </Select>
      <div className="col-span-2">
        <Label required>Reporting Manager</Label>
        <select value={form.reporting_manager_id} onChange={e => {
          const emp = allEmployees.find(x => x.id === e.target.value);
          update('reporting_manager_id', e.target.value);
          update('reporting_manager_name', emp?.name || '');
        }} className={`w-full border rounded-sm px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:border-[#3b4872] ${errors.reporting_manager_id ? 'border-rose-400' : 'border-slate-300'}`}>
          <option value="">— Select —</option>
          {allEmployees.map(e => <option key={e.id} value={e.id}>{e.name} ({e.designation})</option>)}
        </select>
        <FieldError msg={errors.reporting_manager_id} />
      </div>
      <div className="col-span-2">
        <Input label="Engagement Scope (optional)" value={form.scope || ''} onChange={e => update('scope', e.target.value)} placeholder="Brief description of consulting scope" />
      </div>
    </div>
  );

  if (step === 2) return (
    <div className="space-y-5" data-testid="wizard-step-consultant-access">
      <div>
        <Label required>Consultant Profile</Label>
        <select value={form.consultant_profile} onChange={e => update('consultant_profile', e.target.value)}
          className={`w-full border rounded-sm px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:border-[#3b4872] ${errors.consultant_profile ? 'border-rose-400' : 'border-slate-300'}`}
          data-testid="wizard-consultant-profile">
          <option value="">— Select a profile —</option>
          {consultantProfiles.map(p => <option key={p.name} value={p.name}>{p.name}</option>)}
        </select>
        <FieldError msg={errors.consultant_profile} />
        <p className="text-[10px] text-slate-400 mt-1">Manage profiles in Settings → Roles & Access Control → Consultant Profiles</p>
      </div>
      <div>
        <Label required>Project Assignment (required — no all-projects option)</Label>
        <div className="space-y-1 max-h-48 overflow-y-auto border border-slate-200 rounded-sm p-2">
          {projects.map(p => (
            <label key={p.id} className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer hover:bg-slate-50 px-1 py-0.5 rounded">
              <input type="checkbox"
                checked={(form.project_ids || []).includes(p.id)}
                onChange={e => {
                  const ids = new Set(form.project_ids || []);
                  e.target.checked ? ids.add(p.id) : ids.delete(p.id);
                  update('project_ids', [...ids]);
                }} className="accent-[#3b4872]" />
              {p.name}
            </label>
          ))}
        </div>
        <FieldError msg={errors.project_ids} />
      </div>
      {form.contract_end_date && (
        <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 text-xs text-blue-800">
          Access will automatically expire on <strong>{formatDate(form.contract_end_date)}</strong> (contract end date). No manual revocation needed.
        </div>
      )}
    </div>
  );

  if (step === 3) return (
    <div className="space-y-4" data-testid="wizard-step-consultant-review">
      <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs text-amber-900">
        Compensation is <strong>invoice-based</strong> — this consultant will not appear in payroll runs. They can submit consultant invoices through the existing invoice flow.
      </div>
      {[
        { label: 'Identity & Firm', fields: [['Name', form.name], ['Email', form.email], ['Phone', form.phone], ['PAN', form.pan], ['Firm', form.firm_name || '—']] },
        { label: 'Engagement', fields: [['Start', form.doj], ['End', form.contract_end_date], ['Fee', form.gross_salary ? `₹${Number(form.gross_salary).toLocaleString('en-IN')} / ${form.consultant_payment_basis}` : '—']] },
        { label: 'Access', fields: [['Profile', form.consultant_profile || '—'], ['Projects', `${(form.project_ids || []).length} selected`], ['Expires', form.contract_end_date || '—']] },
      ].map(({ label, fields }) => (
        <div key={label} className="bg-slate-50 rounded-sm border border-slate-100 p-4">
          <p className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-2">{label}</p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            {fields.map(([k, v]) => v && (
              <div key={k} className="flex gap-1 text-xs">
                <span className="text-slate-400 w-24 shrink-0">{k}:</span>
                <span className="font-semibold text-slate-700 truncate">{v}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  return null;
}
